import React, { useState, useContext } from "react";
import { useHistory, NavLink } from "react-router-dom";

//pages & includes
import ManageSidebar from "../ManageSidebar";
import Switch from "react-switch";

//functions
import {
  _t,
  getCookie,
  pagination,
  paginationLoading,
  showingData,
  searchedShowingData,
} from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";

//axios and base url
import axios from "axios";
import {
  BASE_URL,
  SAAS_APPLICATION,
} from "../../../../../BaseUrl";

//3rd party packages
import { Helmet } from "react-helmet";
import "react-confirm-alert/src/react-confirm-alert.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

//context consumer
import { SettingsContext } from "../../../../../contexts/Settings";
import { RestaurantContext } from "../../../../../contexts/Restaurant";
import DataTable from "../../../../common/DataTable";

const CompanyList = () => {
  const { t } = useTranslation();
  const history = useHistory();
  //getting context values here
  let {
    //common
    loading,
    setLoading,
  } = useContext(SettingsContext);

  let {
    //company
    companyList,
    setCompanyList,
    getPaginatedCompanyList,
    companyForSearch,
    setCompanyForSearch,
    dataPaginating,
  } = useContext(RestaurantContext);

  // States hook here

  //search result
  let [searchedCompany, setSearchedCompany] = useState({
    list: null,
    searched: false,
  });
 
  const handleUpdateStatus = (company) =>{
    setLoading(true);
    let formData ={
      id: company.id,
      status : company.status ? 0 : 1,
    }
    const companyUrl = BASE_URL + `/settings/update-company-status`;
    return axios
    .post(companyUrl, formData, {
      headers: { Authorization: `Bearer ${getCookie()}` },
    })
    .then((res) => {
      setCompanyList(res.data[0]);
      setCompanyForSearch(res.data[1]);
      setSearchedCompany({
        ...searchedCompany,
        list: res.data[1],
      });
      setLoading(false);
      toast.success(`${_t(t("Company has been updated"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    })
    .catch(() => {
      setLoading(false);
      toast.error(`${_t(t("Please try again"))}`, {
        position: "bottom-center",
        autoClose: 10000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        className: "text-center toast-notification",
      });
    });
  }

  //search company here
  const handleSearch = (e) => {
    let searchInput = e.target.value.toLowerCase();
    if (searchInput.length === 0) {
      setSearchedCompany({ ...searchedCompany, searched: false });
    } else {
      let searchedList = companyForSearch.filter((item) => {
        let lowerCaseItemName = item.name.toLowerCase();
        let lowerCaseItemPhnNo =
          item.phn_no !== null && item.phn_no.toLowerCase();
        let lowerCaseItemAddress =
          item.address !== null && item.address.toLowerCase();
        return (
          lowerCaseItemName.includes(searchInput) ||
          (lowerCaseItemPhnNo && lowerCaseItemPhnNo.includes(searchInput)) ||
          (lowerCaseItemAddress && lowerCaseItemAddress.includes(searchInput))
        );
      });
      setSearchedCompany({
        ...searchedCompany,
        list: searchedList,
        searched: true,
      });
    }
  };

  const columns = [
    {
      header: _t(t("S/L")),
      width: "5%",
      render: (item, index) =>
        index + 1 + ((companyList?.current_page || 1) - 1) * (companyList?.per_page || 1),
    },
    {
      header: _t(t("Name")),
      key: "name",
      width: "25%",
    },
    {
      header: _t(t("Address")),
      render: (item) => item.address || "-",
      width: "25%",
    },
    {
      header: _t(t("Phn no")),
      render: (item) =>
        item.phn_no ? (
          <a href={`tel:${item.phn_no}`} rel="noopener noreferrer">
            {item.phn_no}
          </a>
        ) : (
          "-"
        ),
      width: "15%",
    },
    {
      header: _t(t("Status")),
      render: (item) => (
        <Switch
          checked={JSON.parse(item?.status)}
          name="status"
          onChange={(e) => {
            handleUpdateStatus(item);
          }}
          height={22}
          width={44}
          offColor="#ee5253"
        />
      ),
      width: "15%",
    },
    {
      header: _t(t("Action")),
      render: (item) => (
        <NavLink
          className="btn btn-primary ml-2 rounded-sm"
          to={`/dashboard/manage/restaurant/company-edit/${item.id}`}
          style={{ height: "1.7rem", width: "1.7rem" }}
        >
          <div className="d-flex align-items-center justify-content-center">
            <i className="fa fa-pencil edit-icon" aria-hidden="true"></i>
          </div>
        </NavLink>
      ),
      width: "15%",
    },
  ];

  const tableData = !searchedCompany.searched
    ? companyList?.data || []
    : searchedCompany.list || [];

  const searchPayload = {
    isSearched: searchedCompany.searched,
    searchData: searchedCompany.list || [],
    onClearSearch: () =>
      setSearchedCompany({
        ...searchedCompany,
        searched: false,
      }),
  };

  const paginationObj = {
    isPageLoading: dataPaginating,
    isPagination: !searchedCompany.searched,
    pageCount: companyList?.last_page || 1,
    currentPage: companyList?.current_page || 1,
    from: companyList?.from || 0,
    to: searchedCompany.searched ? searchedCompany.list?.length : (companyList?.to || 0),
    total: companyList?.total || 0,
  };

  return (
    <>
      <Helmet>
        <title>{_t(t("Companies"))}</title>
      </Helmet>

      {/* main body */}
      <main id="main" data-simplebar>
        <div className="container-fluid">
          <div className="row t-mt-10 gx-2">
            {/* left Sidebar */}
            <div className="col-lg-2 col-xxl-2 t-mb-30 mb-lg-0">
              <ManageSidebar />
            </div>
            {/* left Sidebar ends */}

            {/* Rightbar contents */}
            <div className="col-lg-10 col-xxl-10 t-mb-30 mb-lg-0">
              <div className="t-bg-white">
                <div className="fk-scroll--pos-table-menu" data-simplebar>
                  <div className="t-pl-15 t-pr-15">
                    {/* Loading effect */}
                      <div key="fragment3">
                        {/* next page data spin loading */}
                        <div className={`${(loading) && "loadingBlur"}`}></div>
                      <div className={`${(loading) && "lds-roller"}`}><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
                        {/* spin loading ends */}

                        <div className="row gx-2 align-items-center t-pt-15 t-pb-15">
                          <div className="col-md-6 col-lg-5 t-mb-15 mb-md-0">
                            <ul className="t-list fk-breadcrumb">
                              <li className="fk-breadcrumb__list">
                                <span className="t-link fk-breadcrumb__link text-capitalize">
                                  {!searchedCompany.searched
                                    ? _t(t("Company List"))
                                    : _t(t("Search Result"))}
                                </span>
                              </li>
                            </ul>
                          </div>
                          <div className="col-md-6 col-lg-7">
                            <div className="row gx-3 align-items-center">
                              {/* Search group */}
                              <div className="col-md-9 t-mb-15 mb-md-0">
                                <div className="input-group">
                                  <div className="form-file">
                                    <input
                                      type="text"
                                      className="form-control border-0 form-control--light-1 rounded-left"
                                      placeholder={_t(t("Search")) + ".."}
                                      onChange={handleSearch}
                                    />
                                  </div>
                                  <button
                                    className="btn btn-primary rounded-right"
                                    type="button"
                                  >
                                    <i
                                      className="fa fa-search"
                                      aria-hidden="true"
                                    ></i>
                                  </button>
                                </div>
                              </div>

                              {/* Add group modal trigger button */}
                              <div className="col-md-3 text-md-right">
                                {SAAS_APPLICATION == "YES"
                                  ? [(
                                        <NavLink
                                          to="/dashboard/manage/restaurant/company-add"
                                          className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                                        >
                                          {_t(t("add new"))}
                                        </NavLink>
                                    ),
                                    ]
                                  : [
                                      <NavLink
                                        to="/dashboard/manage/restaurant/company-add"
                                        className="btn btn-primary xsm-text text-uppercase btn-lg btn-block rounded"
                                      >
                                        {_t(t("add new"))}
                                      </NavLink>
                                    ]}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Table */}
                        <DataTable
                          columns={columns}
                          data={tableData}
                          loading={loading}
                          emptyText="No data available"
                          pagination={paginationObj}
                          handleFetchData={getPaginatedCompanyList}
                          searchPayload={searchPayload}
                        />
                      </div>                    
                  </div>
                </div>
              </div>
            </div>
            {/* Rightbar contents end*/}
          </div>
        </div>
      </main>
      {/* main body ends */}
    </>
  );
};

export default CompanyList;